import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LANGUAGES } from '../utils/translations';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Heart, Sun, Target, Users, Feather, Moon, Compass, 
  ChevronRight, ChevronLeft, Check, BookOpen, Globe
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  { id: 'peace', labelKey: 'peace', icon: Heart, color: 'text-pink-500 bg-pink-100' },
  { id: 'gratitude', labelKey: 'gratitude', icon: Sun, color: 'text-amber-500 bg-amber-100' },
  { id: 'discipline', labelKey: 'discipline', icon: Target, color: 'text-blue-500 bg-blue-100' },
  { id: 'relationships', labelKey: 'relationships', icon: Users, color: 'text-green-500 bg-green-100' },
  { id: 'forgiveness', labelKey: 'forgiveness', icon: Feather, color: 'text-purple-500 bg-purple-100' },
  { id: 'purpose', labelKey: 'purpose', icon: Compass, color: 'text-indigo-500 bg-indigo-100' },
  { id: 'sleep', labelKey: 'sleep', icon: Moon, color: 'text-slate-500 bg-slate-100' },
];

const BIBLE_VERSIONS = [
  { id: 'web', name: 'World English Bible', abbrev: 'WEB', desc: 'Modern, easy to read' },
  { id: 'kjv', name: 'King James Version', abbrev: 'KJV', desc: 'Classic, traditional' },
  { id: 'bbe', name: 'Bible in Basic English', abbrev: 'BBE', desc: 'Simple vocabulary' },
  { id: 'asv', name: 'American Standard Version', abbrev: 'ASV', desc: 'Literal translation' },
  { id: 'ylt', name: "Young's Literal Translation", abbrev: 'YLT', desc: 'Word-for-word' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    name: user?.name || '',
    faith_mode: 'christian',
    goals: [],
    tone: 'gentle',
    session_length: 'short',
    notification_time: '08:00',
    bible_version: 'web',
    language: language
  });

  const totalSteps = 6;

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const toggleGoal = (goalId) => {
    setData(prev => ({
      ...prev,
      goals: prev.goals.includes(goalId)
        ? prev.goals.filter(g => g !== goalId)
        : [...prev.goals, goalId]
    }));
  };

  const handleComplete = async () => {
    if (data.goals.length === 0) {
      toast.error('Please select at least one goal');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API}/users/onboarding`, data);
      updateUser(response.data);
      toast.success('Welcome to BlessBe! 🙏💙');
      navigate('/home');
    } catch (error) {
      toast.error('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6 animate-fade-in" data-testid="step-language">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <Globe className="w-8 h-8 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('selectLanguage')}</h2>
              <p className="text-gray-500">{t('chooseLanguage')}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setData({ ...data, language: lang.code });
                  }}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${
                    data.language === lang.code
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{lang.flag}</span>
                    <div>
                      <span className="font-bold text-gray-800">{lang.name}</span>
                    </div>
                    {data.language === lang.code && (
                      <Check className="w-5 h-5 text-blue-500 ml-auto" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6 animate-fade-in" data-testid="step-name">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4 overflow-hidden">
                <img src="/images/logo.png" alt="BlessBe" className="w-16 h-16 object-cover" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('welcome')}</h2>
              <p className="text-gray-500">{t('whatToCallYou')}</p>
            </div>
            <Input
              type="text"
              placeholder={t('yourName')}
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              className="h-14 text-center text-xl rounded-2xl bg-gray-50 border-gray-200"
              data-testid="name-input"
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fade-in" data-testid="step-faith">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('faithPath')}</h2>
              <p className="text-gray-500">{t('chooseLanguage')}</p>
            </div>
            <div className="space-y-3">
              {[
                { value: 'christian', labelKey: 'christianFaith', descKey: 'christianDesc' },
                { value: 'general', labelKey: 'generalFaith', descKey: 'generalDesc' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setData({ ...data, faith_mode: option.value })}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                    data.faith_mode === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  data-testid={`faith-${option.value}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-gray-800">{t(option.labelKey)}</p>
                      <p className="text-sm text-gray-500">{t(option.descKey)}</p>
                    </div>
                    {data.faith_mode === option.value && (
                      <Check className="w-5 h-5 text-blue-500" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-fade-in" data-testid="step-goals">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('selectGoals')}</h2>
              <p className="text-gray-500">{t('goalsDesc')}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {GOALS.map(goal => {
                const Icon = goal.icon;
                const isSelected = data.goals.includes(goal.id);
                return (
                  <button
                    key={goal.id}
                    onClick={() => toggleGoal(goal.id)}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                    data-testid={`goal-${goal.id}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${goal.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="font-semibold text-gray-800">{t(goal.labelKey)}</p>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 animate-fade-in" data-testid="step-bible">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <BookOpen className="w-8 h-8 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('bibleTranslation')}</h2>
              <p className="text-gray-500">{t('chooseVersion')}</p>
            </div>
            <div className="space-y-3">
              {BIBLE_VERSIONS.map(version => (
                <button
                  key={version.id}
                  onClick={() => setData({ ...data, bible_version: version.id })}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                    data.bible_version === version.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  data-testid={`bible-${version.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-800">{version.abbrev}</span>
                        <span className="text-sm text-gray-500">- {version.name}</span>
                      </div>
                      <p className="text-sm text-gray-500">{version.desc}</p>
                    </div>
                    {data.bible_version === version.id && (
                      <Check className="w-5 h-5 text-blue-500" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6 animate-fade-in" data-testid="step-preferences">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('preferences')}</h2>
              <p className="text-gray-500">{t('howToGuide')}</p>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="text-sm font-semibold text-gray-600 mb-2 block">Tone</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'gentle', labelKey: 'gentleTone' },
                    { value: 'direct', labelKey: 'directTone' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setData({ ...data, tone: option.value })}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        data.tone === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      data-testid={`tone-${option.value}`}
                    >
                      {t(option.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600 mb-2 block">{t('sessionLength')}</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'short', labelKey: 'shortSession' },
                    { value: 'medium', labelKey: 'mediumSession' },
                    { value: 'long', labelKey: 'longSession' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setData({ ...data, session_length: option.value })}
                      className={`p-3 rounded-xl border-2 transition-all text-sm ${
                        data.session_length === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      data-testid={`length-${option.value}`}
                    >
                      {t(option.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600 mb-2 block">{t('dailyReminder')}</label>
                <Input
                  type="time"
                  value={data.notification_time}
                  onChange={(e) => setData({ ...data, notification_time: e.target.value })}
                  className="h-12 rounded-xl bg-gray-50 border-gray-200"
                  data-testid="notification-time"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className="min-h-screen sky-gradient flex flex-col"
      data-testid="onboarding-page"
    >
      {/* Progress */}
      <div className="p-6">
        <div className="flex gap-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                i <= step ? 'bg-blue-500' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col px-6 pb-6">
        <Card className="flex-1 rounded-3xl border-0 shadow-float bg-white/95 backdrop-blur-sm">
          <CardContent className="p-6 h-full flex flex-col">
            <div className="flex-1">
              {renderStep()}
            </div>

            {/* Navigation */}
            <div className="flex gap-3 mt-6 pt-6 border-t border-gray-100">
              {step > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleBack}
                  className="rounded-full text-gray-600"
                  data-testid="back-button"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  {t('back')}
                </Button>
              )}
              <Button
                type="button"
                onClick={handleNext}
                disabled={loading || (step === 3 && data.goals.length === 0)}
                className="flex-1 h-12 rounded-full text-lg font-bold bg-blue-500 hover:bg-blue-600 shadow-lg"
                data-testid="next-button"
              >
                {loading ? (
                  t('loading')
                ) : step === totalSteps - 1 ? (
                  t('finishSetup')
                ) : (
                  <>
                    {t('continue')}
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
